import React, { useEffect, useState, useCallback } from 'react';
import {
  Box, Grid, Stat, StatLabel, StatNumber, StatHelpText, Heading, Text,
  Spinner, Center, Badge, VStack, HStack, Flex,
} from '@chakra-ui/react';
import {
  LineChart, Line, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
  CartesianGrid,
} from 'recharts';

function StatCard({ label, value, help, color = 'purple.600' }) {
  return (
    <Box bg="white" borderRadius="lg" p={5} boxShadow="sm" borderWidth="1px" borderColor="gray.100">
      <Stat>
        <StatLabel color="gray.500" fontSize="sm">{label}</StatLabel>
        <StatNumber color={color} fontSize="2xl" fontWeight="700">{value}</StatNumber>
        {help && <StatHelpText color="gray.400" fontSize="xs">{help}</StatHelpText>}
      </Stat>
    </Box>
  );
}

function UsageDashboard({ token, apiBase }) {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const resp = await fetch(`${apiBase}/api/v1/stats/`, {
        headers: { Authorization: `Token ${token}` },
      });
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      setStats(await resp.json());
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [token, apiBase]);

  useEffect(() => { load(); }, [load]);

  if (loading) return <Center py={20}><Spinner color="purple.500" size="xl" /></Center>;
  if (error) return <Center py={20}><Text color="red.500">Error: {error}</Text></Center>;
  if (!stats) return null;

  const hourlyData = (stats.hourly_last_24h || []).map(r => ({
    hour: new Date(r.hour).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
    Events: r.count,
  }));

  const dauData = (stats.dau_last_7d || []).map(r => ({
    date: r.date,
    Users: r.users,
  }));

  return (
    <VStack spacing={6} align="stretch">
      <Box>
        <Heading size="lg" color="gray.800">Usage Dashboard</Heading>
        <Text color="gray.500" mt={1}>Platform-wide telemetry overview</Text>
      </Box>

      {/* Summary stats */}
      <Grid templateColumns={{ base: '1fr 1fr', md: 'repeat(4, 1fr)' }} gap={4}>
        <StatCard label="Total Events" value={stats.total.toLocaleString()} />
        <StatCard label="Last 24h" value={stats.last_24h.toLocaleString()} help="events" color="blue.600" />
        <StatCard label="Last 7 days" value={stats.last_7d.toLocaleString()} help="events" color="purple.600" />
        <StatCard label="Last 30 days" value={stats.last_30d.toLocaleString()} help="events" color="orange.500" />
      </Grid>

      {/* By app */}
      <Box bg="white" borderRadius="lg" p={5} boxShadow="sm" borderWidth="1px" borderColor="gray.100">
        <Heading size="sm" color="gray.700" mb={4}>Events by App (last 7 days)</Heading>
        <HStack spacing={3} wrap="wrap">
          {stats.by_app.map(row => (
            <Badge
              key={row.app}
              colorScheme={row.app === 'portal' ? 'teal' : row.app === 'dashboard' ? 'blue' : row.app === 'mobile' ? 'orange' : row.app === 'ticketing' ? 'purple' : 'gray'}
              fontSize="sm"
              px={3}
              py={1}
              borderRadius="md"
            >
              {row.app}: {row.count.toLocaleString()}
            </Badge>
          ))}
        </HStack>
      </Box>

      <Grid templateColumns={{ base: '1fr', lg: '1fr 1fr' }} gap={6}>
        {/* Hourly events */}
        <Box bg="white" borderRadius="lg" p={5} boxShadow="sm" borderWidth="1px" borderColor="gray.100">
          <Heading size="sm" color="gray.700" mb={4}>Events per Hour (last 24h)</Heading>
          {hourlyData.length > 0 ? (
            <ResponsiveContainer width="100%" height={200}>
              <LineChart data={hourlyData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="hour" tick={{ fill: '#718096', fontSize: 11 }} />
                <YAxis tick={{ fill: '#718096', fontSize: 11 }} />
                <Tooltip contentStyle={{ background: '#fff', border: '1px solid #e2e8f0', color: '#2d3748' }} />
                <Line type="monotone" dataKey="Events" stroke="#805AD5" dot={false} strokeWidth={2} />
              </LineChart>
            </ResponsiveContainer>
          ) : (
            <Center h="200px"><Text color="gray.400" fontSize="sm">No data yet</Text></Center>
          )}
        </Box>

        {/* DAU */}
        <Box bg="white" borderRadius="lg" p={5} boxShadow="sm" borderWidth="1px" borderColor="gray.100">
          <Heading size="sm" color="gray.700" mb={4}>Daily Active Users (last 7 days)</Heading>
          {dauData.length > 0 ? (
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={dauData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="date" tick={{ fill: '#718096', fontSize: 11 }} />
                <YAxis tick={{ fill: '#718096', fontSize: 11 }} />
                <Tooltip contentStyle={{ background: '#fff', border: '1px solid #e2e8f0', color: '#2d3748' }} />
                <Bar dataKey="Users" fill="#805AD5" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <Center h="200px"><Text color="gray.400" fontSize="sm">No data yet</Text></Center>
          )}
        </Box>
      </Grid>

      <Grid templateColumns={{ base: '1fr', lg: '1fr 1fr' }} gap={6}>
        {/* Top events */}
        <Box bg="white" borderRadius="lg" p={5} boxShadow="sm" borderWidth="1px" borderColor="gray.100">
          <Heading size="sm" color="gray.700" mb={4}>Top Event Types (last 7 days)</Heading>
          <VStack align="stretch" spacing={2}>
            {stats.top_events.length === 0 && <Text color="gray.400" fontSize="sm">No data yet</Text>}
            {stats.top_events.map((row, i) => (
              <Flex key={row.event_type} justify="space-between" align="center">
                <HStack>
                  <Text color="gray.400" fontSize="xs" w="20px">{i + 1}.</Text>
                  <Text color="gray.700" fontSize="sm">{row.event_type}</Text>
                </HStack>
                <Badge colorScheme="purple">{row.count.toLocaleString()}</Badge>
              </Flex>
            ))}
          </VStack>
        </Box>

        {/* Top tenants */}
        <Box bg="white" borderRadius="lg" p={5} boxShadow="sm" borderWidth="1px" borderColor="gray.100">
          <Heading size="sm" color="gray.700" mb={4}>Top Instances (last 7 days)</Heading>
          <VStack align="stretch" spacing={2}>
            {stats.top_tenants.length === 0 && <Text color="gray.400" fontSize="sm">No data yet</Text>}
            {stats.top_tenants.map((row, i) => (
              <Flex key={row.tenant_id} justify="space-between" align="center">
                <HStack>
                  <Text color="gray.400" fontSize="xs" w="20px">{i + 1}.</Text>
                  <Text color="gray.700" fontSize="sm">{row.tenant_slug || `ID: ${row.tenant_id}`}</Text>
                </HStack>
                <Badge colorScheme="purple">{row.count.toLocaleString()}</Badge>
              </Flex>
            ))}
          </VStack>
        </Box>
      </Grid>
    </VStack>
  );
}

export default UsageDashboard;
